import { useCrew } from '@/context/CrewProvider';
import { trpc } from '@/trpc/react';
import Drawer from 'expo-router/drawer';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { z } from 'zod';
import { router, useLocalSearchParams } from "expo-router";
import Toast from "react-native-toast-message";
import { addAvailabilityFormSchema, mapAvailabilityFormForFrontend, mapCreateCrewAvailabilityForBackend, resolveAvailabilityRange, validateAvailabilityRange } from '@/components/calendar/add-availability-helper';
import AvailabilityForm from '@/components/calendar/availability-form';
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatLondonStamp } from '@/lib/availability-dates';
import { AvailabilityClashAlert } from '@/components/calendar/availability-clash-alert';
import { isPast } from 'date-fns'
import { AvailabilityPastAlert } from '@/components/calendar/availability-past-alert';
import { showAvailabilityError } from '@/components/calendar/availability-toast';
import { busyDayKeys } from '@/components/calendar/availability-colors';
import colors from '@/constants/Colors';
import { Button } from '@/components/ui/button';
import { P } from '@/components/ui/typography';
import { DeleteAvailabilityAlert } from '@/components/calendar/delete-availability-alert';

export default function EditAvailability() {
  const [openClashAlert, setOpenClashAlert] = useState(false);
  const [openPastAlert, setOpenPastAlert] = useState(false);
  const [openDeleteAlert, setOpenDeleteAlert] = useState(false);
  const { id } = useLocalSearchParams();
  const { data: availability, isLoading } = trpc.crew.getCrewAvailabilityById.useQuery(Number(id));

  const mappedAvailability = availability ? mapAvailabilityFormForFrontend(availability) : {};

  const { currentCrewMember } = useCrew();
  const { data: contractTypeHistory } = trpc.crew.listContractTypeHistory.useQuery(Number(currentCrewMember?.id));

  const form = useForm<z.infer<typeof addAvailabilityFormSchema>>({
    resolver: zodResolver(addAvailabilityFormSchema),
    defaultValues: {}
  });

  const formInitialized = useRef(false);
  useEffect(() => {
    if (mappedAvailability && !formInitialized.current && !isLoading) {
      form.reset(mappedAvailability);
      formInitialized.current = true;
    }
  }, [mappedAvailability, isLoading]);

  // find latest contract type where effective date has past
  const latestContractType = contractTypeHistory?.find((contractType) => contractType.date <= new Date())?.type_id;
  const isEmployee = latestContractType ? latestContractType === 1 : currentCrewMember?.contract_type === 1;
  const isAvailability = !currentCrewMember?.is_full_time;

  const { data: crewJobDates } = trpc.jobDateListRouter.listAllCrewJobs.useQuery(Number(currentCrewMember?.id), { enabled: !isEmployee });
  const mappedDates = crewJobDates ? crewJobDates?.data?.filter(date => date.crew_state === 2).map(date => date.date_started_at as Date) : [];

  // Every one of these used to be onSuccess-only, so a failed save looked exactly like no save.
  const { mutate: updateCrewAvailability } = trpc.crew.updateCrewAvailability.useMutation({
    onSuccess: () => {
      router.back();
      Toast.show({
        type: 'success',
        text1: `${isAvailability ? 'Successfully updated availability' : 'Unavailability request submitted for approval'}`,
      });
    },
    onError: (error) => showAvailabilityError(error.message),
  });

  const { mutate: createAvailability } = trpc.crew.createCrewAvailability.useMutation({
    onSuccess: () => {
      router.replace("/(app)/home");
      Toast.show({
        type: 'success',
        text1: `${isAvailability ? 'Successfully added availability' : 'Unavailability request submitted for approval'}`,
      });
    },
    onError: (error) => showAvailabilityError(error.message),
  });

  const { mutate: deleteCrewAvailability } = trpc.crew.deleteCrewAvailability.useMutation({
    onSuccess: () => {
      router.replace("/(app)/home");
      Toast.show({
        type: 'success',
        text1: `${isAvailability ? 'Availability deleted' : 'Unavailability request deleted'}`,
      });
    },
    onError: (error) => showAvailabilityError(error.message),
  });

  const { mutate: createTask } = trpc.task.createTask.useMutation({
    onError: () => showAvailabilityError("We saved your request but could not notify the office. Please send them a message."),
  });

  // A period that has already begun cannot be amended, but it can still be deleted — the Delete
  // button below stays live. Previously the whole screen was unreachable once the start had
  // passed, so a crew member had no way to clear a wrong or duplicated entry themselves.
  const hasStarted = !!availability?.from_date && isPast(availability.from_date);

  // The same dots the add screen shows, minus this period and the rest of its repeat group —
  // a period always overlaps itself, and showing that would just be noise.
  const { data: crewAvailability } = trpc.crew.getCrewAvailability.useQuery(Number(currentCrewMember?.id));
  const busyDays = useMemo(() => {
    const family = new Set([Number(id), availability?.parentId, availability?.recurrence_parent].filter(Boolean) as number[]);
    const excludeIds = (crewAvailability ?? [])
      .filter(item => family.has(item.id) || (item.recurrence_parent && family.has(item.recurrence_parent)))
      .map(item => item.id);

    return busyDayKeys(crewAvailability ?? [], isAvailability, excludeIds);
  }, [crewAvailability, isAvailability, id, availability?.parentId, availability?.recurrence_parent]);

  async function onSubmit(data: z.infer<typeof addAvailabilityFormSchema>, start_date: string, end_date: string) {
    const range = resolveAvailabilityRange(data, start_date, end_date);

    const rangeError = validateAvailabilityRange(range);
    if (rangeError) {
      showAvailabilityError(rangeError);
      return;
    }

    const { from_date: startDate, to_date: endDate } = range;

    if (isPast(startDate)) {
      setOpenPastAlert(true);
      return;
    }
    const hasClashes = mappedDates.some(date => startDate <= date && date <= endDate);

    if (hasClashes && !isAvailability) {
      setOpenClashAlert(true);
      return;
    }
    const mappedAvailability = mapCreateCrewAvailabilityForBackend(data, isEmployee, range, currentCrewMember?.id!, isAvailability);

    if (!mappedAvailability) {
      return;
    }
    const isEditedAvailabilityDenied = availability?.approved === -1;
    isEditedAvailabilityDenied ?
      createAvailability(mappedAvailability) :
      updateCrewAvailability({ ...mappedAvailability, id: Number(id), parentId: availability?.recurrence_parent });

    if (isEmployee) {
      createTask({
        title: `New Crew ${isAvailability ? 'Availability' : 'Unavailability'} request for ${currentCrewMember?.name}`,
        description: `New Crew ${isAvailability ? 'Availability' : 'Unavailability'} request for ${currentCrewMember?.name} from ${formatLondonStamp(startDate)} to ${formatLondonStamp(endDate)}`,
        priority: 1,
        tags: ["crew availability"],
        due_date: startDate,
        crew_id: currentCrewMember?.id,
        state_name: "Open",
        type: "crew_availability.create",
        team_id: 2
      })
    }
  }

  const handleDelete = () => {
    if (id) {
      deleteCrewAvailability(Number(id));
    }
  };

  if (isLoading || !mappedAvailability) {
    return (
      <>
        <Drawer.Screen
          options={{
            title: `Edit ${isAvailability ? 'Availability' : 'Unavailability'}`,
            headerStyle: {
              backgroundColor: colors.primary,
            },
            headerTintColor: colors.accent,
          }}
        />
        <View className="flex-1 justify-center">
          <ActivityIndicator />
        </View>
      </>
    )
  }

  return (
    <>
      <Drawer.Screen
        options={{
          title: `Edit ${isAvailability ? 'Availability' : 'Unavailability'}`,
          headerStyle: {
            backgroundColor: colors.primary,
          },
          headerTintColor: colors.accent,
        }}
      />
      <AvailabilityClashAlert open={openClashAlert} onOpenChange={setOpenClashAlert} />
      <DeleteAvailabilityAlert
        open={openDeleteAlert}
        onOpenChange={setOpenDeleteAlert}
        onConfirm={handleDelete}
        isAvailability={isAvailability}
      />
      <ScrollView className="flex-1 p-4">
        {hasStarted && (
          <View className="mb-4 rounded-lg bg-amber-100 border border-amber-300 p-3">
            <P className="text-amber-900">
              This {isAvailability ? 'availability' : 'unavailability'} has already started, so it can no longer be
              changed. You can still delete it below, or message the office.
            </P>
          </View>
        )}
        <AvailabilityForm form={form} isAvailability={isAvailability} onSubmit={onSubmit} selectedAvailability={mappedAvailability as z.infer<typeof addAvailabilityFormSchema>} disableSubmit={hasStarted} busyDays={busyDays} />
        <View className="">
          <Button variant="destructive" onPress={() => setOpenDeleteAlert(true)}>
            Delete {isAvailability ? 'Availability' : 'Unavailability'}
          </Button>
        </View>
      </ScrollView>
      <AvailabilityPastAlert
        open={openPastAlert}
        onOpenChange={setOpenPastAlert}
        message="You cannot alter availability to a date in the past." />
    </>
  );
}
